import { c as _c } from "react-compiler-runtime";
import { useMemo } from 'react';
import { DEFAULT_PAGE_SIZE } from 'framework-listing-lib/constants/pagination';
import { ASC, DESC } from 'framework-listing-lib/constants/sorts';
import useQuickFilters from 'framework-listing-lib/hooks/useQuickFilters';
import { combineFilters, getAggregatedFilterGroups } from 'framework-listing-lib/utils/filters';
import { useDeletedObjects, useFilterGroups, useFiltersState, useHiddenFilter } from './useListingLibFilters';
import { useHiddenFolderFilter } from 'framework-listing-lib/internal/Folders/hooks/useFolderProps';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { useProperties } from '../internal/providers/PropertiesProvider';
function getDefaultParams(objectTypeId) {
  return {
    count: DEFAULT_PAGE_SIZE,
    offset: 0,
    objectTypeId,
    requestOptions: {
      allPropertiesFetchMode: 'latest_version',
      includeAllProperties: true
    },
    filterGroups: [{
      filters: []
    }],
    query: '',
    sorts: [{
      property: 'hs_created_at',
      order: DESC
    }, {
      property: 'hs_object_id',
      order: ASC
    }]
  };
}
function overrideSorts({
  sort,
  sortPropertyOverride
}) {
  var _sortPropertyOverride2;
  if (!sortPropertyOverride) {
    return [sort];
  }
  const isV1Override = !sortPropertyOverride.propertyMap && !sortPropertyOverride.transform;
  if (isV1Override) {
    var _sortPropertyOverride;
    return [Object.assign({}, sort, {
      property: ((_sortPropertyOverride = sortPropertyOverride[sort.property]) === null || _sortPropertyOverride === void 0 ? void 0 : _sortPropertyOverride.sortKey) || sort.property
    })];
  }
  const mappedSort = Object.assign({}, sort, {
    property: ((_sortPropertyOverride2 = sortPropertyOverride.propertyMap) === null || _sortPropertyOverride2 === void 0 || (_sortPropertyOverride2 = _sortPropertyOverride2[sort.property]) === null || _sortPropertyOverride2 === void 0 ? void 0 : _sortPropertyOverride2.sortKey) || sort.property
  });
  if (sortPropertyOverride.transform) {
    return sortPropertyOverride.transform(mappedSort);
  }
  return [mappedSort];
}

/**
 * Creates fetch params object from the current filter state
 * @param {Object} filters
 */
function getFetchParams({
  deletedObjects,
  filterGroups,
  filtersState,
  hiddenFilter,
  includeHiddenFilter,
  quickFilters,
  sortPropertyOverride,
  timeZoneSource
}) {
  const {
    associationPreviews,
    pagination,
    query,
    requestOptions,
    sort
  } = filtersState;
  const {
    count,
    offset
  } = pagination;
  const fetchParams = {
    count,
    filterGroups: getAggregatedFilterGroups({
      deletedObjects,
      filterGroups,
      hiddenFilter,
      includeHiddenFilter,
      quickFilters
    }),
    offset,
    query,
    timeZoneSource
  };
  if (Object.keys(sort).length) {
    fetchParams.sorts = overrideSorts({
      sort,
      sortPropertyOverride
    });
  }
  if (!fetchParams.query) {
    fetchParams.query = '';
  }
  if (requestOptions) {
    fetchParams.requestOptions = requestOptions;
  }
  if (associationPreviews) {
    fetchParams.associationPreviews = associationPreviews;
  }
  return fetchParams;
}
/**
 * Returns the current crm-search query used in the UI
 */
export default function useCrmSearchQuery(t0) {
  "use memo";

  const $ = _c(20);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {
      includeHiddenFilter: true
    } : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    includeHiddenFilter
  } = t1;
  const objectTypeId = useObjectTypeId();
  const hiddenFilter = useHiddenFilter();
  const deletedObjects = useDeletedObjects();
  const filters = useFiltersState();
  const hiddenFolderFilter = useHiddenFolderFilter();
  const isFoldersMode = useIsFoldersMode();
  const filterGroups = useFilterGroups();
  const quickFilters = useQuickFilters();
  const {
    sortPropertyOverride
  } = useProperties();
  const timeZoneSource = "USER";
  let t2;
  if ($[2] !== hiddenFilter || $[3] !== hiddenFolderFilter || $[4] !== includeHiddenFilter || $[5] !== isFoldersMode) {
    t2 = includeHiddenFilter ? {
      filterGroups: combineFilters(hiddenFilter ? hiddenFilter.filterGroups : [], isFoldersMode && hiddenFolderFilter ? hiddenFolderFilter.filterGroups : [])
    } : undefined;
    $[2] = hiddenFilter;
    $[3] = hiddenFolderFilter;
    $[4] = includeHiddenFilter;
    $[5] = isFoldersMode;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const combinedHiddenFilters = t2;
  const t3 = Boolean(includeHiddenFilter);
  let t4;
  if ($[7] !== combinedHiddenFilters || $[8] !== deletedObjects || $[9] !== filterGroups || $[10] !== filters || $[11] !== quickFilters || $[12] !== sortPropertyOverride || $[13] !== t3) {
    t4 = getFetchParams({
      deletedObjects,
      filterGroups,
      filtersState: filters,
      hiddenFilter: combinedHiddenFilters,
      includeHiddenFilter: t3,
      quickFilters,
      sortPropertyOverride,
      timeZoneSource: "USER"
    });
    $[7] = combinedHiddenFilters;
    $[8] = deletedObjects;
    $[9] = filterGroups;
    $[10] = filters;
    $[11] = quickFilters;
    $[12] = sortPropertyOverride;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const fetchParams = t4;
  let t5;
  if ($[15] !== objectTypeId) {
    t5 = getDefaultParams(objectTypeId);
    $[15] = objectTypeId;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== fetchParams || $[18] !== t5) {
    t6 = Object.assign({}, t5, fetchParams);
    $[17] = fetchParams;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}